// HubSpot property catalog — pure data, no env, consumable by both worker
// (services/hubspot-properties.ts re-export shim) and SPA (admin form
// builder's CrmMappingCombobox). Hoisted out of apps/api in Phase 7
// sub-bucket 11 so the SPA-side CRM-mapping picker can render the same list
// the worker validates against on submission accept.
//
// Phase A ships a hand-curated seed of the properties Bookboost most
// commonly maps fields to. No runtime calls — Phase B will add a
// "Sync from HubSpot" admin button that refreshes this catalog from
// `c.crm.properties.coreApi.getAll()` and writes the result back to the DB.

export type HubSpotObject =
  | "deal"
  | "contact"
  | "company"
  | "partner"
  | "lead";

export type HubSpotPropertyType = "string" | "number" | "enum" | "date";

export type HubSpotPropertyDef = {
  /** HubSpot internal property name (the key used when POSTing properties). */
  property: string;
  /** Human-readable label shown in the combobox. */
  label: string;
  type: HubSpotPropertyType;
};

const dealProperties: readonly HubSpotPropertyDef[] = [
  { property: "dealname", label: "Deal name", type: "string" },
  { property: "dealstage", label: "Deal stage", type: "enum" },
  { property: "pipeline", label: "Pipeline", type: "enum" },
  { property: "amount", label: "Amount", type: "number" },
  { property: "closedate", label: "Close date", type: "date" },
  { property: "dealtype", label: "Deal type", type: "enum" },
  { property: "colab_deal_id", label: "Colab deal ID", type: "string" },
  { property: "hubspot_owner_id", label: "Deal owner", type: "string" },
  { property: "description", label: "Description", type: "string" },
  { property: "createdate", label: "Create date", type: "date" },
  { property: "hs_lastmodifieddate", label: "Last modified", type: "date" },
] as const;

const contactProperties: readonly HubSpotPropertyDef[] = [
  { property: "firstname", label: "First name", type: "string" },
  { property: "lastname", label: "Last name", type: "string" },
  { property: "email", label: "Email", type: "string" },
  { property: "jobtitle", label: "Job title", type: "string" },
  { property: "phone", label: "Phone", type: "string" },
  { property: "company", label: "Company", type: "string" },
  { property: "website", label: "Website", type: "string" },
  { property: "lifecyclestage", label: "Lifecycle stage", type: "enum" },
  { property: "hubspot_owner_id", label: "Contact owner", type: "string" },
  { property: "country", label: "Country", type: "string" },
] as const;

const companyProperties: readonly HubSpotPropertyDef[] = [
  { property: "name", label: "Company name", type: "string" },
  { property: "domain", label: "Domain", type: "string" },
  { property: "industry", label: "Industry", type: "enum" },
  { property: "country", label: "Country", type: "string" },
  { property: "city", label: "City", type: "string" },
  { property: "numberofemployees", label: "Employees", type: "number" },
  { property: "annualrevenue", label: "Annual revenue", type: "number" },
  { property: "website", label: "Website", type: "string" },
  { property: "phone", label: "Phone", type: "string" },
  { property: "description", label: "Description", type: "string" },
  { property: "lifecyclestage", label: "Lifecycle stage", type: "enum" },
] as const;

const partnerProperties: readonly HubSpotPropertyDef[] = [
  { property: "company_name", label: "Company name", type: "string" },
  { property: "partner_type", label: "Partner type", type: "enum" },
  { property: "tier", label: "Tier", type: "enum" },
  { property: "status", label: "Status", type: "enum" },
  { property: "primary_contact_name", label: "Primary contact name", type: "string" },
  { property: "primary_contact_email", label: "Primary contact email", type: "string" },
  { property: "website", label: "Website", type: "string" },
  { property: "region", label: "Region", type: "string" },
] as const;

const leadProperties: readonly HubSpotPropertyDef[] = [
  { property: "hs_lead_name", label: "Lead name", type: "string" },
  { property: "hs_lead_email", label: "Lead email", type: "string" },
  { property: "hs_lead_phone", label: "Lead phone", type: "string" },
  { property: "hs_lead_status", label: "Lead status", type: "enum" },
  { property: "hs_lead_source", label: "Lead source", type: "enum" },
  { property: "hs_lead_owner_id", label: "Lead owner", type: "string" },
  { property: "company", label: "Company", type: "string" },
  { property: "jobtitle", label: "Job title", type: "string" },
  { property: "hs_createdate", label: "Create date", type: "date" },
] as const;

export const HUBSPOT_PROPERTY_REGISTRY: Record<
  HubSpotObject,
  readonly HubSpotPropertyDef[]
> = {
  deal: dealProperties,
  contact: contactProperties,
  company: companyProperties,
  partner: partnerProperties,
  lead: leadProperties,
};

export const HUBSPOT_OBJECT_LABEL: Record<HubSpotObject, string> = {
  deal: "Deal",
  contact: "Contact",
  company: "Company",
  partner: "Partner (custom)",
  lead: "Lead",
};

export const HUBSPOT_OBJECTS: readonly HubSpotObject[] = [
  "deal",
  "contact",
  "company",
  "partner",
  "lead",
] as const;

export function findHubspotProperty(
  object: HubSpotObject,
  property: string,
): HubSpotPropertyDef | null {
  const list = HUBSPOT_PROPERTY_REGISTRY[object];
  return list.find((p) => p.property === property) ?? null;
}
